import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface PublicTenantState {
  tenantId: string | null;
  channelEnabled: boolean;
  loading: boolean;
}

const CACHE_MS = 60 * 1000;
let cache: { host: string; tenantId: string | null; channelEnabled: boolean; ts: number } | null = null;

function getHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname || '';
}

export function usePublicTenant(): PublicTenantState {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [channelEnabled, setChannelEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  const resolve = useCallback(async () => {
    const host = getHostname();
    let now = Date.now();
    if (cache && cache.host === host && now - cache.ts < CACHE_MS) {
      setTenantId(cache.tenantId);
      setChannelEnabled(cache.channelEnabled);
      setLoading(false);
      return;
    }

    setLoading(true);
    now = Date.now();
    try {
      const { data: tid, error: domainError } = await supabase.rpc('get_tenant_id_by_domain', { p_domain: host });
      let resolvedTenantId = !domainError && tid ? tid : null;

      // Om ingen tenant från domän (t.ex. localhost): använd default tenant så att Channel-kollen gäller överallt
      if (!resolvedTenantId) {
        const { data: defaultTid } = await supabase.rpc('get_default_tenant_id');
        resolvedTenantId = defaultTid ?? null;
      }

      let channel = true;
      if (resolvedTenantId) {
        const { data: hasChannel, error: channelError } = await supabase.rpc('get_tenant_has_channel', {
          p_tenant_id: resolvedTenantId,
        });
        channel = !channelError && hasChannel === true;
      }

      cache = { host, tenantId: resolvedTenantId, channelEnabled: channel, ts: now };
      setTenantId(resolvedTenantId);
      setChannelEnabled(channel);
    } catch {
      cache = { host, tenantId: null, channelEnabled: false, ts: now };
      setTenantId(null);
      setChannelEnabled(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    resolve();
  }, [resolve]);

  return { tenantId, channelEnabled, loading };
}
